import React, { forwardRef, useImperativeHandle } from 'react';
import { useIntl } from 'react-intl';
import {
  TextArea,
  TextInput,
  TextInputRefObject,
} from '@dropbox/dig-components/text_fields';
import { UIIcon } from '@dropbox/dig-icons';
import { InfoLine } from '@dropbox/dig-icons/assets';
import styles from 'signer-app/signer-signature-document/accessible-document.module.css';
import TextFieldComponent, {
  getValidTextFieldInput,
} from 'signer-app/signer-signature-document/text-field';
import { FormHelperText } from '@dropbox/dig-components/form_row';
import { isMultilineField } from 'signer-app/signer-signature-document/text-field-hooks';
import { FieldContextProvider } from 'signer-app/signature-request/display-field';
import { messages } from 'signer-app/signer-signature-document/messages';
import textStyles from 'signer-app/signature-request/display-field/text.module.css';

type TextFieldProps = React.ComponentProps<typeof TextFieldComponent>;

type AccessibleTextFieldProps = {
  fieldData: TextFieldProps['fieldData'];
  isActive: TextFieldProps['isActive'];
  updateField: TextFieldProps['updateField'];
  getInstantValidationErrorHack?: TextFieldProps['getInstantValidationErrorHack'];
  onValueChange: (value: string) => void;
  onFocus: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  id?: string;
  testId?: string;
};

/**
 * This is a version of the `TextFieldComponent` that renders the text field as
 * a DIG component but still limits the characters just like we do in the signer
 * experience. In this version, change values come from the DIG component, but
 * the value is forcibly changed by the `TextFieldComponent` as needed
 */
export const AccessibleTextField_ = forwardRef<
  { focus: () => void },
  AccessibleTextFieldProps
>(function AccessibleTextField(
  {
    id,
    testId,
    fieldData,
    isActive,
    updateField,
    getInstantValidationErrorHack,
    onValueChange,
    onFocus,
  },
  parentRef,
) {
  const fieldRef = React.useRef<{ focus: () => void }>(null);
  const lastInputValue = React.useRef<string | null>(null);
  const intl = useIntl();
  const inputRef = React.useRef<TextInputRefObject | HTMLTextAreaElement>(null);

  useImperativeHandle(parentRef, () => ({
    focus: () => {
      inputRef.current?.focus();
    },
  }));

  // We compare the last value from the DIG input to the current value and if
  // they are different then assume the user has reached the character limit
  const isOutOfSpace =
    lastInputValue.current &&
    fieldData.value &&
    lastInputValue.current !== fieldData.value;

  const inputProps = {
    placeholder: fieldData.placeholder,
    value:
      !isActive && fieldData.masked
        ? (fieldData.value ?? '').replace(/./gi, '*')
        : fieldData.value ?? '',
    onFocus,
    'data-form-view-field': fieldData.id,
    'date-testid': testId,
    'data-qa-ref': testId,
    id: id || `field_${fieldData.id}`,
    'aria-describedby': `text_length_hint_${fieldData.id} validation_${fieldData.id}`,
    onChange: (
      evt:
        | React.ChangeEvent<HTMLInputElement>
        | React.FormEvent<HTMLTextAreaElement>,
    ) => {
      // We need to replicate the on change behavior of the signer experience
      // here

      const target = evt.target;
      if (
        !(
          target instanceof HTMLTextAreaElement ||
          target instanceof HTMLInputElement
        )
      ) {
        return;
      }
      const validInput = getValidTextFieldInput(fieldData, target.value);
      if (validInput === null) {
        return;
      }
      lastInputValue.current = validInput;
      onValueChange(validInput);
    },
  };

  const fontFamilyClassName = fieldData.fontFamily
    ? textStyles[`font--${fieldData.fontFamily}`]
    : undefined;

  return (
    <div>
      {isMultilineField(fieldData) ? (
        <TextArea
          className={fontFamilyClassName}
          {...inputProps}
          ref={inputRef as React.RefObject<HTMLTextAreaElement>}
          size="large"
        />
      ) : (
        <TextInput
          className={fontFamilyClassName}
          {...inputProps}
          ref={inputRef as React.RefObject<TextInputRefObject>}
          size="large"
        />
      )}
      {isOutOfSpace ? (
        <FormHelperText
          variant="default"
          id={`text_length_hint_${fieldData.id}`}
        >
          <UIIcon src={InfoLine} size="small" aria-hidden="true" />
          {intl.formatMessage(messages.textTooLong)}
        </FormHelperText>
      ) : null}
      <div
        className={styles.hiddenTextField}
        aria-hidden={true}
        // @ts-ignore `inert` is valid HTML but not in the type definitions
        // This prevents from the children from being focused
        inert="true"
      >
        <FieldContextProvider
          fieldData={fieldData}
          wrapperStyles={{
            width: `${fieldData.width}px`,
            height: `${fieldData.height}px`,
            position: 'relative',
          }}
        >
          <TextFieldComponent
            isActive={isActive}
            updateField={updateField}
            fieldData={fieldData}
            ref={fieldRef as any}
            getInstantValidationErrorHack={getInstantValidationErrorHack}
            documentPreview={false}
            isOverlay={false}
            // This shouldn't be used here as the change events come from the
            // DIG component. Values updated by this component updated via the
            // `updateField` prop
            onChange={(evt) => {
              onValueChange(evt.target.value);
            }}
          />
        </FieldContextProvider>
      </div>
    </div>
  );
});

export const AccessibleTextField = React.memo(AccessibleTextField_);
